import { computed, inject, ref } from 'vue';
import { MAP_COLUMNS_To_Key_Roles, MAP_Key_Roles_To_Show } from '@/views/settings/settingsApiAccess/settingsApiAccess.const';
import { useI18n } from 'vue-i18n';
import {
  Authentication_OrganizationGroupsErrorCode,
  External_Authentication_Type,
  useAuthentication_Get_Organization_GroupsLazyQuery,
  useAuthentication_Has_Azuread_CodeLazyQuery,
  useSettings_ExternalAuth_UpdateGroupRolesMutation,
  useSettings_ExternalAuthsQuery,
  useSettingsPageLinuxQuery,
  useSettingsPageWindowsQuery,
  useUserQuery,
} from '@/graphql/codegen/generated/graphql';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import { useOsService } from '@/services/OsService/OsService';
import { NetworkStatus } from '@apollo/client/core';
import { IKActiveDirectoryManageGroups } from '@/components/common/dialogs/settingsActiveDirectoryDialog/activeDirectory.injection.keys';
import { app } from '@/bootloader';

type GroupMapping = {
  group: string | null | undefined;
  role: string | null | undefined;
  errorMessage: string;
};

export const useManageSecurityGroupsHelpers = () => {
  const { t } = useI18n();
  const coordinatorsService = inject(IKCoordinatorsService);
  const osService = useOsService();
  const GQL_MUTATION_Update_Group_Mapping = useSettings_ExternalAuth_UpdateGroupRolesMutation({});
  const settingsCaller = osService?.isWindows.value ? useSettingsPageWindowsQuery : useSettingsPageLinuxQuery;
  const GQL_get_Active_DirectoryData = useSettings_ExternalAuthsQuery();
  const GQL_Settings = settingsCaller(() => ({
    coordinatorId: coordinatorsService?.selectedCoordinator.value || '',
  }));
  const GQL_userInfo = useUserQuery();

  const authData = computed(() => GQL_Settings?.result?.value?.ibManagerEntities?.settings?.common?.auth);
  const GQL_hasAzureCode = useAuthentication_Has_Azuread_CodeLazyQuery({ fetchPolicy: 'network-only' });

  const isAzureUser = computed(() => authData.value?.authenticationType === External_Authentication_Type.AzureAd);
  const isLocalUser = computed(() => GQL_userInfo.result.value?.user?.local);
  const isLocalAzureUser = computed(() => isAzureUser.value && isLocalUser.value);
  const isLdapUser = computed(() => authData.value?.authenticationType === External_Authentication_Type.Ldap);
  const hasAzureCodeForLocalUser = computed(() => GQL_hasAzureCode.result.value?.authentication_has_azuread_code);
  const force = ref(false);

  const GQL_Groups = useAuthentication_Get_Organization_GroupsLazyQuery(
    () => ({
      data: {
        type: authData.value?.authenticationType || External_Authentication_Type.Ldap,
        force: force.value,
      },
    }),
    { fetchPolicy: 'network-only' },
  );

  const isForbiddenToSeeGroups = computed(() => GQL_Groups?.result?.value?.authentication_get_organization_groups?.errorCode === Authentication_OrganizationGroupsErrorCode.Forbidden);

  const noGroupsAvailableLdap = computed(() => {
    const result = GQL_Groups?.result?.value?.authentication_get_organization_groups.groups;
    return (
      authData?.value?.authenticationType === External_Authentication_Type.Ldap &&
      !GQL_Groups?.result?.value?.authentication_get_organization_groups.errorCode &&
      (result === null || (Array.isArray(result) && result.length === 0))
    );
  });

  const isManualGroupInsert = computed(() => noGroupsAvailableLdap.value && !isForbiddenToSeeGroups.value);

  GQL_Settings.onResult(result => {
    if (result.networkStatus === NetworkStatus.ready) {
      GQL_get_Active_DirectoryData.refetch();
      if (authData?.value?.authenticationType === External_Authentication_Type.AzureAd) {
        GQL_hasAzureCode.load();
      }
      GQL_Groups.load();
    }
  });

  const reloadGroups = async () => {
    force.value = true;
    if (GQL_Groups.networkStatus.value === NetworkStatus.ready) {
      GQL_Groups.refetch();
    } else GQL_Groups.load();
  };

  const existingGroups = computed(() => GQL_Groups?.result?.value?.authentication_get_organization_groups?.groups || []);

  const groupRoleMapping = computed(() => {
    if (authData.value?.authenticationType === External_Authentication_Type.Ldap) {
      return GQL_get_Active_DirectoryData?.result?.value?.settings_externalAuths?.ldapAuth?.groupRoleMapping || [];
    }
    if (authData.value?.authenticationType === External_Authentication_Type.AzureAd) {
      return GQL_get_Active_DirectoryData?.result?.value?.settings_externalAuths?.azureADAuth?.groupRoleMapping || [];
    }
    return [];
  });

  const localGroupsMapping = ref<GroupMapping[]>([]);
  const groupsErrorCounter = computed(() => {
    let counter = 0;
    localGroupsMapping.value.forEach(groupObj => {
      if (groupObj.errorMessage) {
        counter++;
      }
    });
    return counter;
  });

  const getErrorMessage = (index: number, map: GroupMapping[]) => {
    const { group } = map[index];
    if (!group || isForbiddenToSeeGroups.value || isManualGroupInsert.value) {
      return '';
    }
    if (!existingGroups.value.includes(group) && ((isLocalAzureUser.value && hasAzureCodeForLocalUser.value) || (isAzureUser.value && !isLocalUser.value) || isLdapUser.value)) {
      return t('settings.dialogs.connectActiveDirectory.manageSecurityGroups.groupErrors.noGroupFound');
    }
    const groupAlreadyMapped = map.filter((groupObj, i) => groupObj.group === group && i !== index);
    if (groupAlreadyMapped.length > 0) {
      return `Group ${group} ${t('settings.dialogs.connectActiveDirectory.manageSecurityGroups.groupErrors.groupAlreadyMapped')}`;
    }
    return '';
  };

  const currentGroupsMapping = computed(() => {
    if (groupRoleMapping.value.length === 0) {
      localGroupsMapping.value = [
        {
          group: null,
          role: MAP_Key_Roles_To_Show.GridAdmin,
          errorMessage: '',
        },
      ];
      return localGroupsMapping.value;
    }

    const mappedGroups: GroupMapping[] = groupRoleMapping.value.map(groupObj => ({
      group: groupObj?.group,
      role: MAP_Key_Roles_To_Show[groupObj?.role as keyof typeof MAP_Key_Roles_To_Show],
      errorMessage: '',
    }));

    localGroupsMapping.value = mappedGroups.map((groupObj, index) => {
      const errorMessage = getErrorMessage(index, mappedGroups);
      return {
        ...groupObj,
        errorMessage,
      };
    });

    return localGroupsMapping.value;
  });

  const addNewRow = () => {
    localGroupsMapping.value.splice(1, 0, {
      group: null,
      role: MAP_Key_Roles_To_Show.Viewer,
      errorMessage: '',
    });
  };

  const updateGroupSelection = (index: number, group: string) => {
    localGroupsMapping.value[index].group = group;
    localGroupsMapping.value[index].errorMessage = getErrorMessage(index, localGroupsMapping.value);
    localGroupsMapping.value.forEach((groupObj, i) => {
      if (i !== index && !localGroupsMapping.value[index].errorMessage) {
        localGroupsMapping.value[i].errorMessage = getErrorMessage(i, localGroupsMapping.value);
      }
    });
  };

  const updateRoleSelection = (index: number, role: string) => {
    localGroupsMapping.value[index].role = role;
  };

  const isValidForSave = () => {
    localGroupsMapping.value.forEach((groupObj, index) => {
      if (!groupObj.group) {
        localGroupsMapping.value[index].errorMessage = t('settings.dialogs.connectActiveDirectory.manageSecurityGroups.groupErrors.noChosenGroup');
      }
    });
    return groupsErrorCounter.value === 0;
  };

  const saveSecurityGroups = async () => {
    if (isValidForSave()) {
      const payload = localGroupsMapping.value.map(groupObj => ({
        group: groupObj.group,
        role: MAP_COLUMNS_To_Key_Roles[groupObj.role as keyof typeof MAP_COLUMNS_To_Key_Roles],
      }));

      return GQL_MUTATION_Update_Group_Mapping.mutate({
        input: {
          groupRoleMapping: payload,
          authenticationType: authData?.value?.authenticationType || External_Authentication_Type.Ldap,
        },
      });
    }
  };

  const deleteSecurityGroup = async (index: number) => {
    localGroupsMapping.value.splice(index, 1);
    localGroupsMapping.value.forEach((groupObj, i) => {
      localGroupsMapping.value[i].errorMessage = getErrorMessage(i, localGroupsMapping.value);
    });
  };

  const isEditingManageGroupsDisabled = computed(() => (isLocalAzureUser.value && !hasAzureCodeForLocalUser.value) || isForbiddenToSeeGroups.value);

  const showAzureLogin = computed(() => isLocalAzureUser.value && !hasAzureCodeForLocalUser.value && GQL_hasAzureCode.networkStatus.value === NetworkStatus.ready);

  const isDataReadyToShow = computed(() => {
    if (isEditingManageGroupsDisabled.value) {
      return GQL_get_Active_DirectoryData.networkStatus.value === NetworkStatus.ready;
    }
    return GQL_get_Active_DirectoryData.networkStatus.value === NetworkStatus.ready && GQL_Groups.loading.value === false;
  });

  const payload = {
    GQL_Groups,
    GQL_hasAzureCode,
    showAzureLogin,
    existingGroups,
    isDataReadyToShow,
    groupRoleMapping,
    currentGroupsMapping,
    addNewRow,
    isValidForSave,
    deleteSecurityGroup,
    saveSecurityGroups,
    updateGroupSelection,
    updateRoleSelection,
    localGroupsMapping,
    isLocalAzureUser,
    hasAzureCodeForLocalUser,
    isEditingManageGroupsDisabled,
    groupsErrorCounter,
    isForbiddenToSeeGroups,
    isManualGroupInsert,
    reloadGroups,
  };

  app.provide(IKActiveDirectoryManageGroups, payload);
  return payload;
};
